import { useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle, toast } from 'component-library';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePatchJobProduct, usePostJobProduct } from '@/api/rest/resources/jobProducts';
import { JobProduct } from '@/api/rest/resources/types/jobProducts';
import { Form, NumericFormField, TextFormField } from '@/components';
import { JOB_PRODUCTS_QUERY_KEY } from '@/hooks/useJobProducts';

type JobProductFormValues = {
  name: string;
  display_name: string;
  initial_setup_cost_credits: string;
  job_creation_cost_credits: string;
  job_execution_cost_credits: string;
};

type JobProductFormDialogProps = {
  open: boolean;
  onClose: () => void;
  editItem?: JobProduct;
};

export const JobProductFormDialog = ({ open, onClose, editItem }: JobProductFormDialogProps) => {
  const { t } = useTranslation();
  const isEditing = !!editItem;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className='min-w-[30vw]'>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('shared.ndiProducts.dialog.title.update') : t('shared.ndiProducts.dialog.title.create')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>{open && <JobProductFormContent editItem={editItem} onClose={onClose} />}</DialogBody>
      </DialogContent>
    </Dialog>
  );
};

const JobProductFormContent = ({ editItem, onClose }: { editItem?: JobProduct; onClose: () => void }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const postMutation = usePostJobProduct();
  const patchMutation = usePatchJobProduct();
  const invalidNumberMessage = t('shared.ndiProducts.formRules.invalidNumber');

  const form = useForm<JobProductFormValues>({
    defaultValues: {
      name: editItem?.name ?? '',
      display_name: editItem?.display_name ?? '',
      initial_setup_cost_credits: String(editItem?.initial_setup_cost_credits ?? 0),
      job_creation_cost_credits: String(editItem?.job_creation_cost_credits ?? 0),
      job_execution_cost_credits: String(editItem?.job_execution_cost_credits ?? 0),
    },
  });

  const isSubmitting = postMutation.isPending || patchMutation.isPending;

  const handleSubmit = form.handleSubmit(async (values) => {
    const bodyData = {
      name: values.name,
      display_name: values.display_name,
      initial_setup_cost_credits: Number(values.initial_setup_cost_credits),
      job_creation_cost_credits: Number(values.job_creation_cost_credits),
      job_execution_cost_credits: Number(values.job_execution_cost_credits),
    };

    try {
      if (editItem) {
        await patchMutation.mutateAsync({ pathVariables: { id: editItem.id }, bodyData });
      } else {
        await postMutation.mutateAsync({ bodyData });
      }

      queryClient.invalidateQueries({ queryKey: [JOB_PRODUCTS_QUERY_KEY] });
      toast({
        type: 'success',
        title: editItem ? t('shared.ndiProducts.toast.updateSuccess') : t('shared.ndiProducts.toast.createSuccess'),
      });
      onClose();
    } catch {
      toast({ type: 'error', title: t('shared.ndiProducts.toast.saveError') });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
        <TextFormField control={form.control} name='name' label={t('shared.ndiProducts.fields.productName')} />
        <TextFormField control={form.control} name='display_name' label={t('shared.ndiProducts.fields.displayName')} />
        <NumericFormField
          control={form.control}
          name='initial_setup_cost_credits'
          label={t('shared.ndiProducts.fields.initialSetupCostCredits')}
          invalidNumberMessage={invalidNumberMessage}
        />
        <NumericFormField
          control={form.control}
          name='job_creation_cost_credits'
          label={t('shared.ndiProducts.fields.jobCreationCostCredits')}
          invalidNumberMessage={invalidNumberMessage}
        />
        <NumericFormField
          control={form.control}
          name='job_execution_cost_credits'
          label={t('shared.ndiProducts.fields.jobExecutionCostCredits')}
          invalidNumberMessage={invalidNumberMessage}
        />
        <Button type='submit' className='self-end w-fit px-6' loading={isSubmitting} disabled={isSubmitting}>
          {t('global.ui.buttons.save')}
        </Button>
      </form>
    </Form>
  );
};
